import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { hasAnyAdminScope, ROLE_HOME } from './ProtectedRoute';
import { resolvePostAuthDest, clearBookingIntent } from '../lib/returnTo';

const AuthCallback = () => {
  const { exchangeGoogle, exchangeGoogleOAuthCode } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Iter223 — Two possible return shapes:
    //   (A) `?code=...&state=...`  → our own Google OAuth client
    //   (B) `#session_id=...`      → legacy Emergent-managed auth
    // Prefer (A); fall back to (B) so any in-flight legacy redirect
    // still works while both flows co-exist.
    const query = new URLSearchParams(window.location.search || '');
    const code = query.get('code');
    const state = query.get('state');
    const googleErr = query.get('error');

    const finish = async (userPromise) => {
      try {
        const u = await userPromise;
        window.history.replaceState(null, '', window.location.pathname);
        // Iter472 — Google sign-UP is disabled for brand-new users. When the
        // backend reports no existing account (needsCompletion), NO user has
        // been created. Do not route to the profile wall; send them to /login
        // with an inline notice telling them to register with email+password.
        if (u && u.needsCompletion) {
          navigate('/login?google_new=1', { replace: true });
          return;
        }
        // Iter356 — resume a pending booking (sessionStorage intent) if present.
        const resumeDest = resolvePostAuthDest('', null);
        if (resumeDest && !hasAnyAdminScope(u)) {
          clearBookingIntent();
          navigate(resumeDest, { replace: true, state: { user: u } });
          return;
        }
        if (hasAnyAdminScope(u)) {
          navigate(ROLE_HOME[u.role] || '/admin', { replace: true, state: { user: u } });
        } else {
          navigate('/portal', { replace: true, state: { user: u } });
        }
      } catch (err) {
        setError(err.message || 'Google sign-in failed');
        setTimeout(() => navigate('/login', { replace: true }), 2500);
      }
    };

    if (googleErr) {
      setError(googleErr);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
      return;
    }

    if (code) {
      // Validate CSRF state (best-effort — first-time users may not
      // have sessionStorage populated if they came via a different tab).
      try {
        const expected = sessionStorage.getItem('google_oauth_state');
        sessionStorage.removeItem('google_oauth_state');
        if (expected && state && expected !== state) {
          setError('OAuth state mismatch — please try again.');
          setTimeout(() => navigate('/login', { replace: true }), 2500);
          return;
        }
      } catch { /* ignore */ }
      const redirect = window.location.origin + '/auth/callback';
      finish(exchangeGoogleOAuthCode(code, redirect));
      return;
    }

    const hash = window.location.hash || '';
    const m = hash.match(/session_id=([^&]+)/);
    if (m) {
      const sessionId = decodeURIComponent(m[1]);
      finish(exchangeGoogle(sessionId));
      return;
    }

    navigate('/login', { replace: true });
  }, [exchangeGoogle, exchangeGoogleOAuthCode, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      {error ? (
        <div className="text-red-600" data-testid="auth-callback-error">{error}</div>
      ) : (
        <div className="text-gray-600" data-testid="auth-callback-processing">Signing you in…</div>
      )}
    </div>
  );
};

export default AuthCallback;
